import { HttpClient } from '@angular/common/http';
import {
    DataServiceError,
    DefaultDataService,
    DefaultDataServiceConfig,
    HttpUrlGenerator,
    QueryParams
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as fromShared from '../shared';
import {
    DisplayMessageMinimum,
    IDeleteEntity,
    IGenericResponse,
    ResponseStatus
} from '../shared';
import { LoggingService } from '../shared/services/logging/logging.service';
import { DataServiceEntity } from './data-service.entity';
export abstract class EfaactDataService<
    T extends DataServiceEntity<T>
> extends DefaultDataService<T> {
    // protected uri: string;
    protected usePostForQuery = false;
    protected useJSONStringForPost = false;
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        protected entityName: string,
        protected log: LoggingService,
        protected config: DefaultDataServiceConfig,
        protected store: Store
    ) {
        super(entityName, http, httpUrlGenerator, config);
        // this.entityUrl = httpUrlGenerator.entityResource(
        //     entityName,
        //     config.root,
        //     this.trailingSlashEndpoints
        // );
        // this.entitiesUrl = httpUrlGenerator.collectionResource(
        //     entityName,
        //     config.root
        // );

        // this.uri = httpUrlGenerator.collectionResource(entityName, config.root);
        log.verbose({
            type: `${entityName}DataService(EfaactDataService)`,
            msg: '::',
            entityUrl: this.entityUrl,
            entitiesUrl: this.entitiesUrl
        });
    }
    abstract mapItems(item: T): T;
    getAll(): Observable<T[]> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):getAll`
        });
        return super.getAll().pipe(
            map(resp => this.mapGetResponse(resp)),
            map(items => items.map(p => this.mapItems(p))),
            tap(items =>
                this.store.dispatch(
                    new fromShared.Errors.SetErrors(
                        [
                            ...items
                                .filter(
                                    l =>
                                        l &&
                                        l.Status &&
                                        l.Status.StatusCode >=
                                            DisplayMessageMinimum
                                )
                                .map(l => l.Status)
                        ].filter(
                            fromShared.Errors.filterErrors
                        ) as fromShared.IStatusMessage[]
                    )
                )
            ),
            catchError((e, c) =>
                fromShared.Errors.handleDataServiceError(this.store, e, c)
            )
        );
    }
    /**
     * Makes an API call with query parameters, if one parameter sent it uses a GET, otherwise uses POST
     * @param params {string | QueryParams} parameters to send to the API call
     */
    getWithQuery(params: string | QueryParams): Observable<T[]> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):getWithQuery`,
            params
        });
        let result: Observable<T[]>;
        if (this.usePostForQuery) {
            result = this.getWithQueryPOST(params);
        } else {
            if (typeof params === 'string') {
                result = this.getWithQueryGET(params);
            } else {
                const keys = Object.keys(params);
                if (keys.length < 2) {
                    result = this.getWithQueryGET(params);
                } else {
                    result = this.getWithQueryPOST(params);
                }
            }
        }
        return result.pipe(
            tap(items =>
                this.store.dispatch(
                    new fromShared.Errors.SetErrors(
                        [
                            ...items
                                .filter(
                                    l =>
                                        l &&
                                        l.Status &&
                                        l.Status.StatusCode >=
                                            DisplayMessageMinimum
                                )
                                .map(l => l.Status)
                        ].filter(
                            fromShared.Errors.filterErrors
                        ) as fromShared.IStatusMessage[]
                    )
                )
            )
        );
    }
    /**
     * eFAACT API doesn't use this yet, if it starts to will need to override
     * @param id {string | number} id of item to retrieve
     */
    getById(id: string | number): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):getById`
        });
        return super.getById(id).pipe(
            map(
                item => this.mapItems(item),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            )
        );
    }
    /**
     * eFAACT API doesn't use this yet, if it starts to will need to override
     * @param entity {T} entity to add
     */
    public add(entity: T): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):add`
        });
        return super.add(entity).pipe(
            map(
                item => this.mapItems(item),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            )
        );
    }

    /*
     * this is geared around deleting ONE item at a time (to comply with REST standard DELETE requests)
     * delete is optimistic by default (meaning items are removed from the store cache BEFORE http response is received)
     * https://ngrx.io/api/data/EntityDispatcherDefaultOptions
     * need to change this to pessimistic in case of failures.
     */
    delete(key: number | string): Observable<number | string> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):delete`,
            key
        });
        const params = {
            EntityIDs: [key]
        };
        return this.http
            .request<IGenericResponse<IDeleteEntity>>(
                'DELETE',
                this.entitiesUrl,
                {
                    body: params
                }
            )
            .pipe(
                tap(resp =>
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):delete`,
                        resp
                    })
                ),
                map(resp => this.mapPutResponse(resp)),
                tap(resp =>
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):delete`,
                        resp
                    })
                ),
                map(resp => {
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus(resp)
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'DELETE', url: this.entitiesUrl }
                            )
                        );
                    } else {
                        return (resp.Items[0] as any).EntityID;
                    }
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }

    deleteEntity(entity: T): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):delete`,
            entity
        });

        const url = `${this.entitiesUrl}/${entity.id}`;
        return this.http.delete<T>(url, { body: entity }).pipe(
            tap(resp =>
                this.log.verbose({
                    type: `${this.entityName}DataService(EfaactDataService):delete:resp`,
                    resp
                })
            ),
            map(resp => this.mapItems(resp)),
            catchError((e, c) =>
                fromShared.Errors.handleDataServiceError(this.store, e, c)
            )
        );
    }

    update(update: Update<T>): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):update`,
            update
        });
        return this.save(update);
    }
    upsert(entity: T): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):upsert`,
            entity
        });
        return this.save({ id: entity.id, changes: entity } as Update<T>);
    }
    protected save(update: Update<T>): Observable<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):save`,
            update
        });
        const item = {
            ...update.changes
        } as any;
        delete item.original;
        return this.http
            .put<IGenericResponse<T>>(this.entitiesUrl, {
                [`${this.entityName}List`]: [item]
            })
            .pipe(
                map(resp => {
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):save:resp`,
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    update.changes.original &&
                                                    update.changes.original.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );

                        return {
                            ...update.changes,
                            ...update.changes.original,
                            ...resp.Items[0],
                            Status:
                                resp.Items[0] && resp.Items[0].Status
                                    ? resp.Items[0].Status
                                    : {
                                          StatusCode: resp.StatusCode,
                                          Message: resp.StatusType
                                      }
                        } as T;
                    }
                    if (
                        resp.StatusCode === ResponseStatus.Warning &&
                        resp.Items.some(x => !x.Status.MsgEntityID)
                    ) {
                        this.log.verbose({
                            type: `${this.entityName}DataService(EfaactDataService):save:ResponseStatus.Warning`,
                            resp
                        });
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(
                                        x =>
                                            !!x &&
                                            x.Status &&
                                            !x.Status.MsgEntityID
                                    )
                                })
                            )
                        );
                    }
                    const result = {
                        ...this.mapItems({
                            id:
                                update && update.changes && update.changes.id
                                    ? update.changes.id
                                    : update.changes.original
                                    ? update.changes.original.id
                                    : null,
                            ...resp.Items[0]
                        }),
                        Status: {
                            StatusCode: resp.StatusCode,
                            Message:
                                resp.Items &&
                                resp.Items[0] &&
                                resp.Items[0].Status &&
                                resp.Items[0].Status.Message
                                    ? resp.Items[0].Status.Message
                                    : resp.StatusType
                        }
                    } as T;
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):result`,
                        result
                    });
                    return result;
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }
    protected getWithQueryGET(params: string | QueryParams): Observable<T[]> {
        return super.getWithQuery(params).pipe(
            map(resp => this.mapGetResponse(resp)),
            map(items => items.map(item => this.mapItems(item))),
            catchError((e, c) =>
                fromShared.Errors.handleDataServiceError(this.store, e, c)
            )
        );
    }
    protected getWithQueryPOST(params: string | QueryParams): Observable<T[]> {
        let _params = params;
        if (this.useJSONStringForPost) {
            _params = JSON.parse(params as string);
        }
        return this.http.post<T[]>(this.entitiesUrl, _params).pipe(
            tap(resp =>
                this.log.verbose({
                    type: `${this.entityName}DataService(EfaactDataService):getWithQueryPOST:resp`,
                    resp
                })
            ),
            map(resp => this.mapGetResponse(resp)),
            map(items => items.map(item => this.mapItems(item))),
            catchError((e, c) =>
                fromShared.Errors.handleDataServiceError(this.store, e, c)
            )
        );
    }
    protected mapPutResponse(resp: any): IGenericResponse<T> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):mapPutResponse:resp`,
            resp
        });
        return resp as IGenericResponse<T>;
    }
    /**
     * Call this method to translate the response (i.e. from a JSON string to JSON) before mapping each item.
     * @param resp {any} raw response from the API call
     */
    protected mapGetResponse(resp: any): T[] {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):mapGetResponse:resp`,
            resp
        });
        return resp as T[];
    }
}
